import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);
const LOGOUT_HOOKS_MAX_MS = 4000; // tope para cerrar lo que quedo abierto antes de borrar la sesion

export function AuthProvider({ children }) {
  // null = checking session, false = not authenticated, object = authenticated user
  const [user, setUser] = useState(null);

  const fetchMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      setUser(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.pathname.startsWith("/reservas/") || window.location.pathname === "/") {
      setUser(false);
      return;
    }
    fetchMe();
  }, [fetchMe]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    setUser(data);
    return data;
  };

  // Fix AH (2026-10-21): quien tenga algo abierto con la sesion (ej. una llamada en curso) se anota aca para
  // cerrarlo ANTES de borrarla; despues el servidor ya no lo aceptaria. Devuelve como quitarse.
  const logoutHooks = useRef(new Set());
  const onBeforeLogout = useCallback((fn) => {
    logoutHooks.current.add(fn);
    return () => { logoutHooks.current.delete(fn); };
  }, []);

  const logout = async () => {
    const pending = [...logoutHooks.current].map((fn) => {
      try {
        return Promise.resolve(fn());
      } catch (e) {
        return Promise.resolve();
      }
    });
    if (pending.length) {
      // con tope: un cierre que no responde no puede trabar el logout
      let timer;
      const cap = new Promise((resolve) => { timer = setTimeout(resolve, LOGOUT_HOOKS_MAX_MS); });
      await Promise.race([Promise.allSettled(pending), cap]);
      clearTimeout(timer);
    }
    try {
      await api.post("/auth/logout");
    } catch (e) {
      // ignore network errors on logout
    }
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, register, logout, onBeforeLogout, refreshUser: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
